// src/services/StripeCheckoutService.ts
/**
 * Thin client for the `stripe-checkout` edge function.
 *
 * The browser deliberately knows nothing about prices. It sends a logical item
 * id ('base', 'tokens-3m') and receives a Stripe-hosted URL to redirect to.
 * Every price lookup, customer creation and — crucially — every entitlement
 * write happens server-side. Nothing this file does can grant a plan.
 */

import { supabase } from '../config/supabase';
import { TOKEN_PACKS } from './TajirPlanService';

/** Items the checkout function will accept. Mirrors PRICE_ENV in the function. */
export type CheckoutItem = 'base' | 'tokens-1m' | 'tokens-3m' | 'tokens-10m';

export const CHECKOUT_ITEMS: CheckoutItem[] = [
  'base',
  ...TOKEN_PACKS.map((pack) => pack.id as CheckoutItem),
];

export interface CheckoutResult {
  ok: boolean;
  url?: string;
  /** Human-readable reason, safe to show in a toast. */
  error?: string;
  /** True when billing simply is not switched on yet (secrets unset). */
  notConfigured?: boolean;
}

export class StripeCheckoutService {
  /**
   * Ask the edge function for a Checkout Session URL.
   * Does not redirect — the caller decides, so it can show a toast on failure
   * instead of navigating away from a broken state.
   */
  static async createSession(item: CheckoutItem): Promise<CheckoutResult> {
    try {
      const origin = window.location.origin;

      const { data, error } = await supabase.functions.invoke<{ url?: string; error?: string }>(
        'stripe-checkout',
        {
          body: {
            item,
            // `billing` is the tab id SettingsPage actually accepts — anything
            // else is ignored and drops the payer on the profile tab instead.
            successUrl: `${origin}/settings?tab=billing&checkout=success`,
            cancelUrl: `${origin}/settings?tab=billing&checkout=cancelled`,
          },
        },
      );

      if (error) {
        // supabase-js folds non-2xx into `error` and hides the JSON body, so
        // the specific "not configured yet" case has to be recovered from the
        // response itself — otherwise the owner sees a generic failure while
        // the real cause is an unset secret.
        const status = (error as { context?: { status?: number } })?.context?.status;
        const body = await StripeCheckoutService.readErrorBody(error);

        if (status === 503) {
          return { ok: false, notConfigured: true, error: body || 'Billing is not switched on yet.' };
        }
        return { ok: false, error: body || error.message || 'Could not start checkout.' };
      }

      if (!data?.url) {
        return { ok: false, error: data?.error || 'Checkout did not return a payment link.' };
      }

      return { ok: true, url: data.url };
    } catch (err: any) {
      console.error('❌ createSession failed:', err?.message || err);
      return { ok: false, error: 'Could not reach the payment service.' };
    }
  }

  /** Create the session and send the browser to Stripe. */
  static async redirectToCheckout(item: CheckoutItem): Promise<CheckoutResult> {
    const result = await StripeCheckoutService.createSession(item);
    if (result.ok && result.url) {
      window.location.href = result.url;
    }
    return result;
  }

  /** Pull the `error` field out of a FunctionsHttpError response, if present. */
  private static async readErrorBody(error: unknown): Promise<string | null> {
    try {
      const response = (error as { context?: Response })?.context;
      if (!response || typeof response.json !== 'function') return null;
      const parsed = await response.json();
      return typeof parsed?.error === 'string' ? parsed.error : null;
    } catch {
      return null;
    }
  }
}

export default StripeCheckoutService;
